import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import clsx from "clsx";
import React, { Fragment, useRef } from "react";

import { Icon } from "../Icon";
import type { IconsType } from "../Icon/utils";
import Tooltip from "../Tooltip";

export interface ContextMenuItemProps {
  children?: React.ReactNode;
  disabled?: boolean;
  disabledTooltipMessage?: string;
  icon?: IconsType;
  label: string;
  onClick: () => void;
}

export interface ContextMenuProps {
  className?: string;
  items: ContextMenuItemProps[];
  menuItemsClassName?: string;
  position?: "left" | "right";
  trigger?: React.ReactNode;
}

export default function ContextMenu({
  trigger,
  items,
  className,
  menuItemsClassName,
  position = "right",
}: ContextMenuProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);

  return (
    <Menu
      as="div"
      className={clsx("relative inline-block cursor-pointer", className)}
    >
      {({ open }) => (
        <>
          <MenuButton ref={buttonRef} className="flex self-center">
            {trigger || (
              <Icon
                name="ellipsis"
                className="h-4 w-4 p-1 mx-auto hover:bg-gray-100 transition-all ease-in-out duration-100 rounded-md"
              />
            )}
          </MenuButton>
          <Transition
            show={open}
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <MenuItems
              static
              className={clsx(
                "absolute z-10 min-w-max origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none",
                position === "right" ? "right-0" : "left-0",
                "mt-0 lg:mt-2",
                menuItemsClassName,
              )}
            >
              {items.map((item, index) => {
                const content = (
                  <div
                    className={clsx(
                      "block cursor-pointer items-center p-2 py-2 lg:py-1 text-xs lg:text-sm font-normal text-gray-600 hover:bg-gray-200 leading-none whitespace-nowrap w-[150px] max-w-full overflow-hidden text-ellipsis",
                      item.disabled &&
                        "opacity-50 cursor-not-allowed hover:bg-transparent",
                    )}
                  >
                    {item.icon && (
                      <Icon
                        name={item.icon}
                        className="h-3 lg:h-4 mr-2 inline-block"
                      />
                    )}
                    {item.children || item.label}
                  </div>
                );

                return (
                  <MenuItem key={index} disabled={item.disabled}>
                    {() => (
                      <div onClick={item.onClick}>
                        {item.disabled && item.disabledTooltipMessage ? (
                          <Tooltip
                            text={item.disabledTooltipMessage}
                            position="top"
                          >
                            {content}
                          </Tooltip>
                        ) : (
                          content
                        )}
                      </div>
                    )}
                  </MenuItem>
                );
              })}
            </MenuItems>
          </Transition>
        </>
      )}
    </Menu>
  );
}
